/**
 * Card grid sizing.
 *
 * The old version returned a fixed width per breakpoint — `(width - 32) / 2` on xs and a flat
 * 150px otherwise. On xs that consumed the available width *exactly*: two cards plus their 8px
 * margins added up to the container width with zero slack, so a single subpixel of rounding
 * dropped the second card onto its own row. That is the "sometimes only one item per row" bug.
 *
 * The model here is Plezy's: pick a target cell width from the breakpoint, derive a whole column
 * count from it, then divide the *real* width by that count. The item width is therefore always a
 * divisor of the space that exists, and the leftover pixel is given away rather than overflowed.
 *
 * On top of that the user can pin an explicit column count. A 1–5 "density" was tried first and
 * was a bad control: at 412px, densities 2, 3 and 4 all resolved to three columns, so three of
 * the five steps did nothing. Columns are also simply what people mean when they ask how many
 * books fit side by side.
 */

/** Horizontal margin a grid item adds on each side (`mx-2` in ItemBrowser). */
export const CARD_GUTTER = 8

export const COLUMNS_MIN = 1
/**
 * Deliberately generous — a 5120px ultrawide fits well over twenty readable covers, and capping
 * at a round number would be an arbitrary limit on a screen we cannot see. The real limit is
 * [MIN_CARD_WIDTH]: `computeGrid` refuses to make cards unreadably small, and the control greys
 * out "+" once that bites, so the cap adjusts itself to the screen instead of to a constant.
 */
export const COLUMNS_MAX = 30

/** Below this a cover is no longer recognisable, so extra columns stop being useful. */
export const MIN_CARD_WIDTH = 72

/** Narrow layouts and wide ones keep separate column counts — one number cannot serve both. */
export type GridScope = 'compact' | 'wide'

export interface GridColumnsOverride {
  compact: number | null
  wide: number | null
}

export const GRID_COLUMNS_AUTO: GridColumnsOverride = {compact: null, wide: null}

export function gridScopeFor(breakpoint: string): GridScope {
  return breakpoint === 'xs' || breakpoint === 'sm' ? 'compact' : 'wide'
}

/** Target width of one cell when no column count is pinned. */
function targetCardWidth(breakpoint: string): number {
  switch (breakpoint) {
    case 'xs':
      return 140
    case 'sm':
      return 160
    case 'md':
      return 170
    default:
      return 180
  }
}

export interface GridGeometry {
  columns: number
  width: number
}

export function clampColumns(columns: number): number {
  return Math.min(COLUMNS_MAX, Math.max(COLUMNS_MIN, Math.round(columns)))
}

/** How many columns still leave a readable cover in `containerWidth` px. */
export function maxColumnsFor(containerWidth: number): number {
  return Math.max(COLUMNS_MIN, Math.floor(Math.max(0, containerWidth) / (MIN_CARD_WIDTH + CARD_GUTTER * 2)))
}

/**
 * Column count and per-item width for a row of `containerWidth` px.
 *
 * Without an override the count uses the same shape as Flutter's max-cross-axis-extent delegate
 * (`ceil(extent / (target + gutter))`), which lands on the count whose cells are closest to — and
 * never wider than — the target. A pinned count wins, but is still capped at what leaves cards
 * wide enough to show a cover.
 */
export function computeGrid(containerWidth: number, breakpoint: string, override?: number | null): GridGeometry {
  const gutter = CARD_GUTTER * 2
  const available = Math.max(0, containerWidth)
  let columns: number
  if (override != null) {
    columns = Math.min(clampColumns(override), maxColumnsFor(available))
  } else {
    columns = clampColumns(Math.ceil(available / (targetCardWidth(breakpoint) + gutter)))
  }
  // floor, so a fractional remainder can never push the last card onto the next row
  const width = Math.max(MIN_CARD_WIDTH, Math.floor(available / columns) - gutter)
  return {columns, width}
}
